import { useState, useEffect, useRef } from "react";
import { Download, X, Share } from "lucide-react";

function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

function isInStandaloneMode() {
  return window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
}

function isIOSSafari() {
  const ua = navigator.userAgent;
  return isIOS() && /Safari/.test(ua) && !/CriOS|FxiOS|OPiOS|EdgiOS/.test(ua);
}

export default function InstallBanner() {
  const [show, setShow] = useState(false);
  const [mode, setMode] = useState(null); // "native" | "ios-safari" | "ios-other"
  const deferredPrompt = useRef(null);

  useEffect(() => {
    if (localStorage.getItem("unexa_pwa_dismissed")) return;
    if (isInStandaloneMode()) return; // Already installed

    if (isIOS()) {
      // Delay iOS banner slightly so it doesn't flash on page load
      const timer = setTimeout(() => {
        setMode(isIOSSafari() ? "ios-safari" : "ios-other");
        setShow(true);
      }, 2000);
      return () => clearTimeout(timer);
    }

    // Android / Desktop Chrome — listen for native prompt
    const handler = (e) => {
      e.preventDefault();
      deferredPrompt.current = e;
      setMode("native");
      setShow(true);
    };

    window.addEventListener("beforeinstallprompt", handler);
    window.addEventListener("appinstalled", () => {
      setShow(false);
      deferredPrompt.current = null;
      localStorage.setItem("unexa_pwa_dismissed", "installed");
    });

    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt.current) return;
    deferredPrompt.current.prompt();
    const { outcome } = await deferredPrompt.current.userChoice;
    if (outcome === "accepted") {
      localStorage.setItem("unexa_pwa_dismissed", "installed");
    }
    deferredPrompt.current = null;
    setShow(false);
  };

  const handleDismiss = () => {
    setShow(false);
    localStorage.setItem("unexa_pwa_dismissed", Date.now().toString());
  };

  if (!show) return null;

  // iOS on non-Safari browser (Chrome, Firefox, etc.)
  if (mode === "ios-other") {
    return (
      <div
        data-testid="pwa-install-banner"
        className="fixed bottom-20 left-1/2 -translate-x-1/2 z-[9999] w-[calc(100%-2rem)] max-w-sm animate-in slide-in-from-bottom-4 fade-in duration-300"
      >
        <div className="bg-white rounded-2xl shadow-[0_8px_32px_rgba(0,0,0,0.12)] border border-slate-100 p-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center flex-shrink-0">
              <span className="text-white font-extrabold text-lg">U</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-900">Get Unexa App</p>
              <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                Open this page in <strong>Safari</strong>, then tap{" "}
                <Share size={12} className="inline -mt-0.5 text-blue-600" />{" "}
                Share and <strong>"Add to Home Screen"</strong>
              </p>
            </div>
            <button
              data-testid="pwa-dismiss-button"
              onClick={handleDismiss}
              className="p-1 text-slate-400 hover:text-slate-600 transition-colors flex-shrink-0"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // iOS Safari — guide to Add to Home Screen
  if (mode === "ios-safari") {
    return (
      <div
        data-testid="pwa-install-banner"
        className="fixed bottom-20 left-1/2 -translate-x-1/2 z-[9999] w-[calc(100%-2rem)] max-w-sm animate-in slide-in-from-bottom-4 fade-in duration-300"
      >
        <div className="bg-white rounded-2xl shadow-[0_8px_32px_rgba(0,0,0,0.12)] border border-slate-100 p-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center flex-shrink-0">
              <span className="text-white font-extrabold text-lg">U</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-900">Add Unexa to Home Screen</p>
              <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                Tap{" "}
                <Share size={12} className="inline -mt-0.5 text-blue-600" />{" "}
                at the bottom, then <strong>"Add to Home Screen"</strong>
              </p>
            </div>
            <button
              data-testid="pwa-dismiss-button"
              onClick={handleDismiss}
              className="p-1 text-slate-400 hover:text-slate-600 transition-colors flex-shrink-0"
            >
              <X size={16} />
            </button>
          </div>
          {/* Visual arrow pointing to Safari share button */}
          <div className="flex justify-center mt-2">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="text-blue-500 animate-bounce">
              <path d="M12 4L12 20M12 20L6 14M12 20L18 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        </div>
      </div>
    );
  }

  // Native install prompt (Android Chrome / Desktop)
  return (
    <div
      data-testid="pwa-install-banner"
      className="fixed bottom-20 left-1/2 -translate-x-1/2 z-[9999] w-[calc(100%-2rem)] max-w-sm animate-in slide-in-from-bottom-4 fade-in duration-300"
    >
      <div className="bg-white rounded-2xl shadow-[0_8px_32px_rgba(0,0,0,0.12)] border border-slate-100 p-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center flex-shrink-0">
          <span className="text-white font-extrabold text-lg">U</span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-900">Get Unexa App</p>
          <p className="text-xs text-slate-500 truncate">Fast access from your home screen</p>
        </div>
        <button
          data-testid="pwa-install-button"
          onClick={handleInstall}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-xl transition-colors flex items-center gap-1.5 flex-shrink-0"
        >
          <Download size={14} />
          Install
        </button>
        <button
          data-testid="pwa-dismiss-button"
          onClick={handleDismiss}
          className="p-1 text-slate-400 hover:text-slate-600 transition-colors flex-shrink-0"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
